import React from 'react';
import { Box, Tabs, Tab } from '@mui/material';

/*
  SegmentedControl: the translucent pill view/period switcher used in page
  headers. Extracted from the Tabs recipe previously copy-pasted across
  Dashboard / BudgetView / Budgets / ActionManager / UpcomingTransactions —
  and replaces the tiny Select those pages fell back to on mobile, so every
  option stays visible at one tap on all breakpoints.

  Props:
   - options: [{ value, label }]
   - value: the selected option's value
   - onChange: (value) => void — value-based, not index-based
   - fullWidth: spread the segments evenly across the container width
     (for a row that spans the header) instead of hugging their labels
   - sx: overrides for the pill wrapper
*/

export default function SegmentedControl({ options, value, onChange, fullWidth = false, sx }) {
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  return (
    <Box
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: 6,
        p: 0.5,
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        ...(fullWidth && { width: '100%' }),
        ...sx,
      }}
    >
      <Tabs
        value={selectedIndex === -1 ? false : selectedIndex}
        onChange={(e, idx) => onChange(options[idx].value)}
        variant={fullWidth ? 'standard' : 'scrollable'}
        scrollButtons={false}
        sx={{
          minHeight: '32px',
          ...(fullWidth && { width: '100%', '& .MuiTabs-flexContainer': { width: '100%' } }),
          '& .MuiTab-root': {
            color: 'white',
            minWidth: 'auto',
            minHeight: '32px',
            whiteSpace: 'nowrap',
            // Proportional share of the row: longer labels ("This Month") get
            // the room they need instead of wrapping inside an equal split.
            ...(fullWidth && { flex: '1 1 auto', maxWidth: 'none', px: 1 }),
            py: 0.5,
            px: 1.25,
            borderRadius: 6,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            opacity: 0.85,
          },
          '& .Mui-selected': {
            bgcolor: 'white',
            color: 'primary.main',
            opacity: 1,
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {options.map((option) => (
          <Tab key={option.value} label={option.label} />
        ))}
      </Tabs>
    </Box>
  );
}
